import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ApiRowOf } from "@AxiosWrapper";
import type { UserDetails } from "@ServerTypes/accounts/accounts.schema";
import { DEFAULT_TIME_ZONE } from "@Helpers/timeZone";

const initialState: UserDetails = {};

/**
 * One `/accounts/getUserDetails` row as this slice stores it, for web's action and mobile's two
 * dispatch sites alike. The route passes stored nulls through, while `userDetailsSchema` declares
 * those members optional only, so a null becomes an absent value — what readers already get for an
 * account that lacks the key. `isRegistered` is 0/1 on most accounts that carry it, and is read
 * nowhere in web or mobile. `portal` is kept only as the requesting portal's membership: a request
 * that resolved no portal gets the account's whole `portal[]` array, which holds no membership of the
 * page's portal. Every other member is passed through unchanged.
 *
 * `timeZone` is added here, the one place web and mobile resolve it: the zone of the requesting
 * portal's membership, else DEFAULT_TIME_ZONE. A request that resolved no portal has no such zone.
 *
 * ⚠️ KNOWN BUG, open, found 2026-10-28: `timeZone` is a second name for the membership's `portal.timeZone` (with
 * the default filled in), so the slice holds the zone twice and the two can differ: Preferences.tsx `handleTimeZone`
 * updates `timeZone` only, leaving `portal.timeZone` on the old zone until the next getUserDetails (a reload). No
 * reader uses `portal.timeZone` yet (25 web files read `timeZone`), so nothing shows the difference today. Fix: read
 * the zone through one selector (`portal?.timeZone ?? DEFAULT_TIME_ZONE`) and drop the copy, or make
 * `handleTimeZone` update both.
 */
export const toUserDetails = ({
  portal,
  ...row
}: NonNullable<ApiRowOf<"get", "/accounts/getUserDetails">>): UserDetails => ({
  ...row,
  firstName: row.firstName ?? undefined,
  lastName: row.lastName ?? undefined,
  email: row.email ?? undefined,
  contactNo: row.contactNo ?? undefined,
  designation: row.designation ?? undefined,
  userLocation: row.userLocation ?? undefined,
  profilePic: row.profilePic ?? undefined,
  signature: row.signature ?? undefined,
  messageFooter: row.messageFooter ?? undefined,
  isRegistered: row.isRegistered == null ? undefined : Boolean(row.isRegistered),
  ...(portal && !Array.isArray(portal) && { portal }),
  ...(!Array.isArray(portal) && { timeZone: portal?.timeZone ?? DEFAULT_TIME_ZONE }),
});

/**
 * Whether the user's role in the requesting portal holds the portal permission `permissionId` (its id in
 * getPermissions(), packages/helpers/constants.ts): the membership's stored `portal.role.permissions`.
 */
export const holdsPortalPermission = (userDetails: Pick<UserDetails, "portal">, permissionId: number): boolean =>
  userDetails.portal?.role?.permissions?.includes(permissionId) === true;

const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    setUserDetails: (_state: UserDetails, action: PayloadAction<UserDetails>) => ({ ...action.payload }),
    updateUserDetails: (state: UserDetails, action: PayloadAction<Partial<UserDetails>>) => ({
      ...state,
      ...action.payload,
    }),
    resetUserDetails: () => initialState,
  },
});

const { setUserDetails: _setUserDetailsRaw, updateUserDetails: _updateUserDetailsRaw } = userDetailsSlice.actions;
export const { resetUserDetails } = userDetailsSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setUserDetails = <T extends Exact<UserDetails, T>>(payload: T) => _setUserDetailsRaw(payload);
export const updateUserDetails = <T extends Exact<Partial<UserDetails>, T>>(payload: T) =>
  _updateUserDetailsRaw(payload);

export default userDetailsSlice.reducer;
